/**
 * API Service Layer
 * Axios-based service for all backend API calls
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { reportApiError } from '../utils/errorReporting';
import type {
  EscalationChain,
  CreateChainRequest,
  ResolutionPreview,
  IssueTypeCreateRequest,
  IssueTypeUpdateRequest,
  AssetTypeEligibilityUpdate,
  AssetReporterOverrideUpdate,
} from '../types/admin';
import type { IssueTypeDef } from '../types/issue';
import {
  ApiError,
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  User,
  Tenant,
  Asset,
  AssetType,
  AssetCreateRequest,
  FormTemplate,
  FormSubmission,
  TemplateLibraryItem,
  TemplateLibraryFilter,
  CloneTemplateRequest,
  ConvertToTemplateRequest,
  QRNFCToken,
  Notification,
  DashboardStats,
  PaginatedResponse,
  AssetFormLink,
  AssetFormLinkRequest,
  BulkAssetFormLinkRequest,
  AssetTypeFormTemplateRequest,
  AssetFormLinkFilter,
  MaintenanceSchedule,
  MaintenanceTask,
  CreateMaintenanceScheduleRequest,
  UpdateMaintenanceScheduleRequest,
  UpdateMaintenanceTaskRequest,
  MaintenanceScheduleListResponse,
  MaintenanceTaskListResponse,
  // Public Issue Reporting
  Issue,
  CreatePublicIssueRequest,
  UpdatePublicIssueRequest,
  PublicIssueReportResponse,
  TrackingLookupResponse,
  PublicIssueStats,
  PublicAssetInfo,
  IssueStatus,
  IssueType,
  // Submission Comments
  SubmissionComment,
  // Asset Type Maintenance Templates
  AssetTypeMaintenanceTemplate,
  CreateAssetTypeMaintenanceTemplateRequest,
  UpdateAssetTypeMaintenanceTemplateRequest,
  ApplyTemplatesResponse,
  // File Upload
  PresignedUploadRequest,
  PresignedUploadResponse,
  PresignedDownloadRequest,
  PresignedDownloadResponse,
  FileInfo,
  ListFilesRequest,
  ListFilesResponse,
  FileUploadResponse,
  CreateCommentRequest,
  UpdateCommentRequest,
  CommentsListResponse,
  ApprovalHistoryResponse,
} from '../types';

// ============================================================================
// AXIOS INSTANCE CONFIGURATION
// ============================================================================

// Dynamic API URL detection for mobile access
// When accessed via network IP (e.g., 192.168.x.x), use that IP for API calls
// This enables mobile devices on the same network to access the backend
function getApiBaseUrl(): string {
  // Use VITE_API_URL from environment (set in .env.production)
  const envUrl = import.meta.env.VITE_API_URL;

  // If we have an explicit env URL, use it
  if (envUrl) {
    return envUrl;
  }

  // Fallback for local development: use the same hostname with backend port
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  return `${protocol}//${hostname}:8084/api/v1`;
}

const API_BASE_URL = getApiBaseUrl();

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor - add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          console.debug(`🟢 API Request: ${config.method?.toUpperCase()} ${config.url} [AUTH: YES]`);
        } else {
          console.debug(`🟡 API Request: ${config.method?.toUpperCase()} ${config.url} [AUTH: NO]`);
        }

        return config;
      },
      (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle errors
    this.client.interceptors.response.use(
      (response) => {
        console.debug(`API Response: ${response.status} ${response.config.url}`);
        return response;
      },
      (error: AxiosError<ApiError>) => {
        const status = error.response?.status;
        const url = error.config?.url || '';
        const method = error.config?.method || 'unknown';

        console.error('API Error:', {
          url,
          method,
          status,
          message: error.message,
        });

        // Report API errors to admin console (exclude auth errors and 404s)
        // Don't report if this is an error reporting call itself (prevent recursion)
        if (status && status !== 401 && status !== 404 && !url.includes('/admin/errors')) {
          reportApiError(
            url,
            method,
            status,
            error.message,
            error.response?.data
          );
        }

        if (status === 401) {
          // Skip 401 handling if not in browser (SSR/build time)
          if (typeof window === 'undefined') {
            return Promise.reject(this.handleError(error));
          }

          // Unauthorized - only redirect if not already on login/register page
          const currentPath = window.location.pathname;
          const errorData: any = error.response?.data;
          const errorMessage = errorData?.error?.message || errorData?.message || 'Unknown error';

          console.warn('🔴 401 Unauthorized:', {
            path: currentPath,
            url: error.config?.url,
            message: errorMessage,
            hasToken: !!localStorage.getItem('auth_token'),
            timestamp: new Date().toISOString(),
          });

          // Don't redirect if already on auth pages
          if (currentPath !== '/login' && currentPath !== '/register') {
            // Check if we actually have a token
            const hasToken = !!localStorage.getItem('auth_token');

            if (hasToken) {
              // Token exists but was rejected
              // IMPORTANT: Only logout if this is a real auth failure, not a race condition

              // Check if error is "Missing authorization header" - this means token wasn't sent
              // This could be a race condition, so DON'T logout immediately
              if (errorMessage.includes('Missing authorization header')) {
                console.error('🔴 CRITICAL: Token in localStorage but not sent in request!');
                console.error('This indicates a bug in the axios interceptor or a race condition');
                // Don't logout - this is a bug, not an auth failure
                return Promise.reject(this.handleError(error));
              }

              // If we get "Invalid or expired token", then logout
              if (errorMessage.includes('Invalid') || errorMessage.includes('expired')) {
                console.warn('Token rejected by server - clearing auth and redirecting to login');
                this.clearAuthToken();
                sessionStorage.setItem('redirectAfterLogin', currentPath);
                window.location.href = '/login';
              }
            } else {
              // No token yet - might be during initial load, let ProtectedRoute handle it
              console.warn('401 but no token in storage - letting ProtectedRoute handle redirect');
            }
          }
        }
        return Promise.reject(this.handleError(error));
      }
    );
  }

  // ============================================================================
  // AUTH TOKEN MANAGEMENT
  // ============================================================================

  private getAuthToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('auth_token');
  }

  private setAuthToken(token: string): void {
    if (typeof window === 'undefined') return;
    console.log('🔵 API: setAuthToken called, token length:', token?.length || 0);
    localStorage.setItem('auth_token', token);
    console.log('✅ API: Token stored in localStorage');
  }

  private clearAuthToken(): void {
    if (typeof window === 'undefined') return;
    console.log('🔴 API: clearAuthToken called - REMOVING ALL AUTH DATA');
    console.trace('Stack trace for clearAuthToken:');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
    console.log('🔴 API: All auth data removed from localStorage');
  }

  // ============================================================================
  // ERROR HANDLING
  // ============================================================================

  private handleError(error: AxiosError<ApiError>): ApiError {
    if (error.response) {
      // Server responded with error
      return {
        error: error.response.data.error || 'Server Error',
        message: error.response.data.message || error.message,
        status: error.response.status,
        details: error.response.data.details,
      };
    } else if (error.request) {
      // Request made but no response
      return {
        error: 'Network Error',
        message: 'No response from server. Please check your connection.',
        status: 0,
      };
    } else {
      // Something else happened
      return {
        error: 'Request Error',
        message: error.message,
        status: 0,
      };
    }
  }

  // ============================================================================
  // AUTHENTICATION
  // ============================================================================

  async login(credentials: LoginRequest): Promise<AuthResponse> {
    console.log('🔵 API: Attempting login...');
    const response = await this.client.post<AuthResponse>('/auth/login', credentials);
    console.log('✅ API: Login successful, storing auth data');
    console.log('🔵 API: Token length:', response.data.access_token?.length || 0);
    this.setAuthToken(response.data.access_token);
    if (typeof window !== 'undefined') {
      localStorage.setItem('user', JSON.stringify(response.data.user));
      // Only store tenant if it exists
      if (response.data.tenant) {
        localStorage.setItem('tenant', JSON.stringify(response.data.tenant));
      } else {
        localStorage.removeItem('tenant');
      }
    }
    console.log('✅ API: Auth data stored in localStorage');
    return response.data;
  }

  async register(data: RegisterRequest): Promise<AuthResponse> {
    console.log('🔵 API: Attempting registration...');
    const response = await this.client.post<AuthResponse>('/auth/register', data);
    console.log('✅ API: Registration successful, storing auth data');
    console.log('🔵 API: Token length:', response.data.access_token?.length || 0);
    this.setAuthToken(response.data.access_token);
    if (typeof window !== 'undefined') {
      localStorage.setItem('user', JSON.stringify(response.data.user));
      // Only store tenant if it exists
      if (response.data.tenant) {
        localStorage.setItem('tenant', JSON.stringify(response.data.tenant));
      } else {
        localStorage.removeItem('tenant');
      }
    }
    console.log('✅ API: Auth data stored in localStorage');
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    console.log('🔵 API: Attempting password change...');
    const response = await this.client.post('/auth/password/change', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    console.log('✅ API: Password changed successfully');
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.client.post('/auth/logout');
    } finally {
      this.clearAuthToken();
    }
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<User>('/auth/me');
    return response.data;
  }

  // ============================================================================
  // USERS
  // ============================================================================

  async getUsers(params?: { role?: string; search?: string }): Promise<User[]> {
    const response = await this.client.get<any>('/users', { params });
    // Handle both array and paginated response
    if (Array.isArray(response.data)) {
      return response.data;
    }
    // Paginated response: backend writes the list under "data" ({data: [...], total, page, limit}).
    // Keep "users" as a fallback in case an older/alternate shape is returned.
    return response.data?.data || response.data?.users || [];
  }

  async getUser(id: string): Promise<User> {
    const response = await this.client.get<User>(`/users/${id}`);
    return response.data;
  }

  async createUser(data: Partial<User> & { password: string }): Promise<User> {
    const response = await this.client.post<User>('/users', data);
    return response.data;
  }

  async updateUser(id: string, data: Partial<User>): Promise<User> {
    const response = await this.client.put<User>(`/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id: string): Promise<void> {
    await this.client.delete(`/users/${id}`);
  }

  // ============================================================================
  // TENANTS
  // ============================================================================

  async getTenant(): Promise<Tenant> {
    const response = await this.client.get<Tenant>('/tenant');
    return response.data;
  }

  async updateTenant(data: Partial<Tenant>): Promise<Tenant> {
    const response = await this.client.put<Tenant>('/tenant', data);
    return response.data;
  }

  // ============================================================================
  // ASSETS
  // ============================================================================

  // ============================================================================
  // SITES
  // ============================================================================

  async getSites(params?: { search?: string; city?: string; region?: string; is_active?: string; page?: number; limit?: number }): Promise<any> {
    const response = await this.client.get<any>('/sites', { params });
    return response.data;
  }

  async getSite(id: string): Promise<any> {
    const response = await this.client.get<any>(`/sites/${id}`);
    return response.data;
  }

  async createSite(data: any): Promise<any> {
    const response = await this.client.post<any>('/sites', data);
    return response.data;
  }

  async updateSite(id: string, data: any): Promise<any> {
    const response = await this.client.put<any>(`/sites/${id}`, data);
    return response.data;
  }

  async deleteSite(id: string): Promise<void> {
    await this.client.delete(`/sites/${id}`);
  }

  async getSiteUsers(siteId: string): Promise<any> {
    const response = await this.client.get<any>(`/sites/${siteId}/users`);
    return response.data;
  }

  async assignUserToSite(siteId: string, data: { user_id: string; role?: string; is_primary?: boolean }): Promise<any> {
    const response = await this.client.post<any>(`/sites/${siteId}/users`, data);
    return response.data;
  }

  async removeUserFromSite(siteId: string, userId: string): Promise<void> {
    await this.client.delete(`/sites/${siteId}/users/${userId}`);
  }

  async getSiteSummary(siteId: string): Promise<any> {
    const response = await this.client.get<any>(`/sites/${siteId}/summary`);
    return response.data;
  }

  async getUserSites(userId: string): Promise<any> {
    const response = await this.client.get<any>(`/users/${userId}/sites`);
    return response.data;
  }

  // ============================================================================
  // ASSETS
  // ============================================================================

  async getAssets(params?: {
    asset_type?: string;
    status?: string;
    search?: string;
    site_id?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Asset>> {
    const response = await this.client.get<any>('/assets', { params });
    // Backend returns {data, total, page, limit} - data is the array of assets
    // Frontend expects {data, total, page, limit, total_pages}
    return {
      data: response.data.data || response.data.assets || [],
      total: response.data.total || 0,
      page: response.data.page || 1,
      limit: response.data.limit || 20,
      total_pages: Math.ceil((response.data.total || 0) / (response.data.limit || 20)),
    };
  }

  async getAsset(id: string): Promise<Asset> {
    const response = await this.client.get<any>(`/assets/${id}?include_relations=true`);
    // Backend returns {success, message, asset: {...}}
    return response.data.asset || response.data;
  }

  async createAsset(data: AssetCreateRequest): Promise<Asset> {
    const response = await this.client.post<Asset>('/assets', data);
    return response.data;
  }

  async updateAsset(id: string, data: Partial<AssetCreateRequest>): Promise<Asset> {
    const response = await this.client.put<Asset>(`/assets/${id}`, data);
    return response.data;
  }

  async deleteAsset(id: string): Promise<void> {
    await this.client.delete(`/assets/${id}`);
  }

  async archiveAsset(id: string): Promise<Asset> {
    const response = await this.client.post<Asset>(`/assets/${id}/archive`);
    return response.data;
  }

  async unarchiveAsset(id: string): Promise<Asset> {
    const response = await this.client.post<Asset>(`/assets/${id}/unarchive`);
    return response.data;
  }

  async getAssetSubAssets(id: string): Promise<Asset[]> {
    const response = await this.client.get<Asset[]>(`/assets/${id}/sub-assets`);
    return response.data;
  }

  // ============================================================================
  // ASSET TYPES
  // ============================================================================

  async getAssetTypes(): Promise<AssetType[]> {
    // Backend AssetTypeListResponse serializes the list under "data" (json:"data").
    const response = await this.client.get<any>('/asset-types');
    return response.data?.data || response.data?.asset_types || [];
  }

  async getAssetType(id: string): Promise<AssetType> {
    // Backend AssetTypeGetResponse wraps the entity: {success, message, asset_type}.
    const response = await this.client.get<any>(`/asset-types/${id}`);
    return response.data?.asset_type || response.data;
  }

  async createAssetType(data: { name: string; description?: string; icon?: string; color?: string }): Promise<AssetType> {
    const response = await this.client.post<AssetType>('/asset-types', data);
    return response.data;
  }

  async updateAssetType(id: string, data: Partial<{ name: string; description?: string; icon?: string; color?: string }>): Promise<AssetType> {
    const response = await this.client.put<AssetType>(`/asset-types/${id}`, data);
    return response.data;
  }

  async deleteAssetType(id: string): Promise<void> {
    await this.client.delete(`/asset-types/${id}`);
  }

  // ============================================================================
  // ASSET TYPE MAINTENANCE TEMPLATES
  // ============================================================================

  async getAssetTypeMaintenanceTemplates(assetTypeId: string): Promise<AssetTypeMaintenanceTemplate[]> {
    const response = await this.client.get<AssetTypeMaintenanceTemplate[]>(`/asset-types/${assetTypeId}/maintenance-templates`);
    return response.data || [];
  }

  async createAssetTypeMaintenanceTemplate(assetTypeId: string, data: CreateAssetTypeMaintenanceTemplateRequest): Promise<AssetTypeMaintenanceTemplate> {
    const response = await this.client.post<AssetTypeMaintenanceTemplate>(`/asset-types/${assetTypeId}/maintenance-templates`, data);
    return response.data;
  }

  async updateAssetTypeMaintenanceTemplate(assetTypeId: string, templateId: string, data: UpdateAssetTypeMaintenanceTemplateRequest): Promise<AssetTypeMaintenanceTemplate> {
    const response = await this.client.put<AssetTypeMaintenanceTemplate>(`/asset-types/${assetTypeId}/maintenance-templates/${templateId}`, data);
    return response.data;
  }

  async deleteAssetTypeMaintenanceTemplate(assetTypeId: string, templateId: string): Promise<void> {
    await this.client.delete(`/asset-types/${assetTypeId}/maintenance-templates/${templateId}`);
  }

  async applyMaintenanceTemplates(assetTypeId: string, skipExisting: boolean = true): Promise<ApplyTemplatesResponse> {
    const response = await this.client.post<ApplyTemplatesResponse>('/maintenance-templates/apply', {
      asset_type_id: assetTypeId,
      skip_existing: skipExisting,
    });
    return response.data;
  }

  // ============================================================================
  // FORMS / TEMPLATES
  // ============================================================================

  async getForms(params?: {
    status?: string;
    category?: string;
    is_template?: boolean;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<FormTemplate>> {
    const response = await this.client.get<PaginatedResponse<FormTemplate>>('/forms', { params });
    return response.data;
  }

  async getForm(id: string): Promise<FormTemplate> {
    const response = await this.client.get<FormTemplate>(`/forms/${id}`);
    return response.data;
  }

  async createForm(data: Partial<FormTemplate>): Promise<FormTemplate> {
    const response = await this.client.post<FormTemplate>('/forms', data);
    return response.data;
  }

  async updateForm(id: string, data: Partial<FormTemplate>): Promise<FormTemplate> {
    const response = await this.client.put<FormTemplate>(`/forms/${id}`, data);
    return response.data;
  }

  async deleteForm(id: string): Promise<void> {
    await this.client.delete(`/forms/${id}`);
  }

  async publishForm(id: string): Promise<FormTemplate> {
    const response = await this.client.post<FormTemplate>(`/forms/${id}/publish`);
    return response.data;
  }

  async archiveForm(id: string): Promise<FormTemplate> {
    const response = await this.client.post<FormTemplate>(`/forms/${id}/archive`);
    return response.data;
  }

  async unarchiveForm(id: string): Promise<FormTemplate> {
    const response = await this.client.post<FormTemplate>(`/forms/${id}/unarchive`);
    return response.data;
  }

  async duplicateForm(id: string, name?: string): Promise<FormTemplate> {
    const response = await this.client.post<FormTemplate>(`/forms/${id}/duplicate`, { name });
    return response.data;
  }

  // ============================================================================
  // TEMPLATE LIBRARY
  // ============================================================================

  async getTemplateLibrary(filter?: TemplateLibraryFilter): Promise<{
    templates: TemplateLibraryItem[];
    total_count: number;
  }> {
    const response = await this.client.get('/templates/library', { params: filter });
    return response.data;
  }

  async getTemplate(id: string): Promise<TemplateLibraryItem> {
    const response = await this.client.get<TemplateLibraryItem>(`/templates/${id}`);
    return response.data;
  }

  async cloneTemplate(id: string, request?: CloneTemplateRequest): Promise<FormTemplate> {
    const response = await this.client.post<FormTemplate>(`/templates/${id}/clone`, request);
    return response.data;
  }

  async convertToTemplate(
    formId: string,
    request: ConvertToTemplateRequest
  ): Promise<FormTemplate> {
    const response = await this.client.post<FormTemplate>(
      `/forms/${formId}/convert-to-template`,
      request
    );
    return response.data;
  }

  async getMyTemplates(params?: {
    category?: string;
    is_public?: boolean;
  }): Promise<FormTemplate[]> {
    const response = await this.client.get<FormTemplate[]>('/templates/my-templates', { params });
    return response.data;
  }

  // ============================================================================
  // FORM SUBMISSIONS
  // ============================================================================

  async getSubmissions(params?: {
    form_id?: string;
    asset_id?: string;
    user_id?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<FormSubmission>> {
    const response = await this.client.get<PaginatedResponse<FormSubmission>>('/submissions', {
      params,
    });
    return response.data;
  }

  async getSubmission(id: string): Promise<FormSubmission> {
    const response = await this.client.get<FormSubmission>(`/submissions/${id}`);
    return response.data;
  }

  async createSubmission(data: {
    form_template_id: string;
    asset_id?: string;
    submission_data: Record<string, any>;
  }): Promise<FormSubmission> {
    const response = await this.client.post<FormSubmission>('/submissions', data);
    return response.data;
  }

  async updateSubmission(
    id: string,
    data: {
      submission_data: Record<string, any>;
      status?: string;
    }
  ): Promise<FormSubmission> {
    const response = await this.client.put<FormSubmission>(`/submissions/${id}`, data);
    return response.data;
  }

  async approveSubmission(id: string, notes?: string): Promise<FormSubmission> {
    const response = await this.client.post<FormSubmission>(`/submissions/${id}/approve`, {
      notes,
    });
    return response.data;
  }

  async rejectSubmission(id: string, notes: string): Promise<FormSubmission> {
    const response = await this.client.post<FormSubmission>(`/submissions/${id}/reject`, {
      notes,
    });
    return response.data;
  }

  // ============================================================================
  // QR/NFC
  // ============================================================================

  async generateQRCode(assetId: string, formId?: string): Promise<QRNFCToken> {
    // Create/get asset URL which can be used for QR code generation
    // If formId is provided, creates a form-specific QR code
    const requestBody = formId ? { form_template_id: formId } : {};
    const response = await this.client.post<any>(`/assets/${assetId}/url`, requestBody);
    // Map backend AssetURLInfo to frontend QRNFCToken
    return {
      id: response.data.id,
      tenant_id: response.data.tenant_id,
      asset_id: response.data.asset_id,
      token: response.data.token,
      token_type: 'qr',
      is_active: true,
      scan_count: response.data.scan_count || 0,
      last_scanned_at: response.data.last_scanned_at,
      created_at: response.data.created_at,
      qr_code_url: response.data.url, // Backend uses 'url' field
    };
  }

  async generateNFCTag(assetId: string): Promise<QRNFCToken> {
    // Same as QR code - create/get asset URL
    const response = await this.client.post<any>(`/assets/${assetId}/url`);
    return {
      id: response.data.id,
      tenant_id: response.data.tenant_id,
      asset_id: response.data.asset_id,
      token: response.data.token,
      token_type: 'nfc',
      is_active: true,
      scan_count: response.data.scan_count || 0,
      last_scanned_at: response.data.last_scanned_at,
      created_at: response.data.created_at,
      qr_code_url: response.data.url,
    };
  }

  // ============================================================================
  // ASSET-FORM LINKING
  // ============================================================================

  async listAssetFormLinks(filter?: AssetFormLinkFilter): Promise<{
    links: AssetFormLink[];
    total: number;
  }> {
    const response = await this.client.get<{
      links: AssetFormLink[];
      total: number;
      success: boolean;
    }>('/asset-form-links', { params: filter });
    return {
      links: response.data.links || [],
      total: response.data.total || 0,
    };
  }

  async createAssetFormLink(data: AssetFormLinkRequest): Promise<AssetFormLink> {
    const response = await this.client.post<{ link: AssetFormLink; success: boolean }>(
      '/asset-form-links',
      data
    );
    return response.data.link;
  }

  async bulkCreateAssetFormLinks(data: BulkAssetFormLinkRequest): Promise<AssetFormLink[]> {
    const response = await this.client.post<{ links: AssetFormLink[]; success: boolean }>(
      '/asset-form-links/bulk',
      data
    );
    return response.data.links || [];
  }

  async linkFormToAssetType(data: AssetTypeFormTemplateRequest): Promise<{
    count: number;
    asset_ids: string[];
  }> {
    const response = await this.client.post<{
      count: number;
      asset_ids: string[];
      success: boolean;
    }>('/asset-form-links/asset-type', data);
    return {
      count: response.data.count,
      asset_ids: response.data.asset_ids || [],
    };
  }

  async updateAssetFormLink(
    id: string,
    data: Partial<AssetFormLinkRequest>
  ): Promise<AssetFormLink> {
    const response = await this.client.put<{ link: AssetFormLink; success: boolean }>(
      `/asset-form-links/${id}`,
      data
    );
    return response.data.link;
  }

  async deleteAssetFormLink(id: string): Promise<void> {
    await this.client.delete(`/asset-form-links/${id}`);
  }

  async getAssetForms(assetId: string): Promise<AssetFormLink[]> {
    const response = await this.client.get<{ links: AssetFormLink[]; success: boolean }>(
      `/assets/${assetId}/forms`
    );
    return response.data.links || [];
  }

  async getFormAssets(formId: string): Promise<AssetFormLink[]> {
    const response = await this.client.get<{ links: AssetFormLink[]; success: boolean }>(
      `/forms/${formId}/assets`
    );
    return response.data.links || [];
  }

  // ============================================================================
  // NOTIFICATIONS
  // ============================================================================

  async getNotifications(params?: {
    is_read?: boolean;
    limit?: number;
  }): Promise<Notification[]> {
    const response = await this.client.get<Notification[]>('/notifications', { params });
    return response.data;
  }

  async markNotificationAsRead(id: string): Promise<void> {
    await this.client.put(`/notifications/${id}/read`);
  }

  async markAllNotificationsAsRead(): Promise<void> {
    await this.client.put('/notifications/read-all');
  }

  // ============================================================================
  // DASHBOARD & ANALYTICS
  // ============================================================================

  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.client.get<DashboardStats>('/dashboard/stats');
    return response.data;
  }

  async getAssetAnalytics(assetId: string, period: 'week' | 'month' | 'year'): Promise<any> {
    const response = await this.client.get(`/analytics/assets/${assetId}`, {
      params: { period },
    });
    return response.data;
  }

  async getFormAnalytics(formId: string, period: 'week' | 'month' | 'year'): Promise<any> {
    const response = await this.client.get(`/analytics/forms/${formId}`, { params: { period } });
    return response.data;
  }

  async getAnalyticsDashboard(params?: { timeframe?: string; date_from?: string; date_to?: string }): Promise<any> {
    const response = await this.client.get('/analytics/dashboard', { params });
    return response.data;
  }

  async getAnalyticsTrends(params?: { timeframe?: string; grouping?: string; date_from?: string; date_to?: string }): Promise<any> {
    const response = await this.client.get('/analytics/trends', { params });
    return response.data;
  }

  async getAnalyticsPerformance(params?: { timeframe?: string }): Promise<any> {
    const response = await this.client.get('/analytics/performance', { params });
    return response.data;
  }

  // ============================================================================
  // BILLING
  // ============================================================================

  async getSubscriptionPlans(): Promise<any> {
    const response = await this.client.get('/billing/plans');
    return response.data;
  }

  async getActiveSubscription(): Promise<any> {
    const response = await this.client.get('/billing/subscriptions/active');
    return response.data;
  }

  async getBillingInfo(): Promise<any> {
    const response = await this.client.get('/billing/info');
    return response.data;
  }

  async createSubscription(data: { plan_id: string }): Promise<any> {
    const response = await this.client.post('/billing/subscriptions', data);
    return response.data;
  }

  async cancelSubscription(subscriptionId: string): Promise<any> {
    const response = await this.client.post(`/billing/subscriptions/${subscriptionId}/cancel`);
    return response.data;
  }

  async getInvoices(params?: { limit?: number; offset?: number }): Promise<any> {
    const response = await this.client.get('/billing/invoices', { params });
    return response.data;
  }

  // ============================================================================
  // FILE UPLOADS
  // ============================================================================

  async uploadFile(file: File, type: 'image' | 'document'): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const response = await this.client.post<{ url: string }>('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async uploadFiles(files: File[], type: 'image' | 'document'): Promise<{ urls: string[] }> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('type', type);

    const response = await this.client.post<{ urls: string[] }>('/files/upload-multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // ============================================================================
  // MAINTENANCE SCHEDULES
  // ============================================================================

  async getMaintenanceSchedules(params?: {
    asset_id?: string;
    asset_type_id?: string;
    status?: string;
    trigger_type?: string;
    assigned_to_user_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<MaintenanceScheduleListResponse> {
    const response = await this.client.get<MaintenanceScheduleListResponse>('/maintenance/schedules', {
      params,
    });
    return response.data;
  }

  async getMaintenanceSchedule(id: string): Promise<MaintenanceSchedule> {
    const response = await this.client.get<MaintenanceSchedule>(`/maintenance/schedules/${id}`);
    return response.data;
  }

  async createMaintenanceSchedule(
    data: CreateMaintenanceScheduleRequest
  ): Promise<MaintenanceSchedule> {
    const response = await this.client.post<MaintenanceSchedule>('/maintenance/schedules', data);
    return response.data;
  }

  async updateMaintenanceSchedule(
    id: string,
    data: UpdateMaintenanceScheduleRequest
  ): Promise<MaintenanceSchedule> {
    const response = await this.client.put<MaintenanceSchedule>(
      `/maintenance/schedules/${id}`,
      data
    );
    return response.data;
  }

  async deleteMaintenanceSchedule(id: string): Promise<void> {
    await this.client.delete(`/maintenance/schedules/${id}`);
  }

  // ============================================================================
  // MAINTENANCE TASKS
  // ============================================================================

  async getMaintenanceTasks(params?: {
    schedule_id?: string;
    asset_id?: string;
    status?: string;
    assigned_to_user_id?: string;
    due_before?: string;
    due_after?: string;
    limit?: number;
    offset?: number;
  }): Promise<MaintenanceTaskListResponse> {
    const response = await this.client.get<MaintenanceTaskListResponse>('/maintenance/tasks', {
      params,
    });
    return response.data;
  }

  async getMaintenanceTask(id: string): Promise<MaintenanceTask> {
    const response = await this.client.get<MaintenanceTask>(`/maintenance/tasks/${id}`);
    return response.data;
  }

  async createMaintenanceTask(data: {
    schedule_id: string;
    due_date: string;
  }): Promise<MaintenanceTask> {
    const response = await this.client.post<MaintenanceTask>('/maintenance/tasks', data);
    return response.data;
  }

  async updateMaintenanceTask(
    id: string,
    data: UpdateMaintenanceTaskRequest
  ): Promise<MaintenanceTask> {
    const response = await this.client.put<MaintenanceTask>(`/maintenance/tasks/${id}`, data);
    return response.data;
  }

  async getUpcomingMaintenanceTasks(days?: number): Promise<{
    tasks: MaintenanceTask[];
    days_ahead: number;
  }> {
    const response = await this.client.get('/maintenance/tasks/upcoming', {
      params: { days },
    });
    return response.data;
  }

  async getOverdueMaintenanceTasks(): Promise<{
    tasks: MaintenanceTask[];
    count: number;
  }> {
    const response = await this.client.get('/maintenance/tasks/overdue');
    return response.data;
  }

  // ==========================================================================
  // PUBLIC ISSUE REPORTING (External User Access - No Auth Required)
  // ==========================================================================

  /**
   * Get public asset information by token (no auth required)
   * Used when public users scan QR codes
   */
  async getPublicAsset(token: string): Promise<PublicAssetInfo> {
    const response = await this.client.get(`/public/assets/${token}`);
    return response.data;
  }

  /**
   * Create a public issue report (no auth required, but requires reCAPTCHA)
   * Used when public users report issues via QR codes
   */
  async createPublicIssue(
    token: string,
    data: CreatePublicIssueRequest
  ): Promise<PublicIssueReportResponse> {
    const response = await this.client.post(`/public/assets/${token}/issues`, data);
    return response.data;
  }

  /**
   * Track issue status by tracking number (no auth required)
   * Public endpoint for users to check their issue status
   */
  async trackIssue(trackingNumber: string): Promise<TrackingLookupResponse> {
    const response = await this.client.get(`/public/issues/track/${trackingNumber}`);
    return response.data;
  }

  // ==========================================================================
  // PUBLIC ISSUE MANAGEMENT (Admin/Tenant - Auth Required)
  // ==========================================================================

  /**
   * List all public issues for a tenant (admin only)
   */
  async listPublicIssues(params?: {
    asset_id?: string;
    status?: IssueStatus;
    issue_type?: IssueType;
    assigned_to?: string;
    is_spam?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{
    success: boolean;
    data: Issue[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const response = await this.client.get('/issues', { params });
    return response.data;
  }

  /**
   * Get a single public issue by ID (admin only)
   */
  async getPublicIssueById(issueId: string): Promise<{
    success: boolean;
    data: Issue;
  }> {
    const response = await this.client.get(`/issues/${issueId}`);
    return response.data;
  }

  /**
   * Update a public issue (admin only)
   */
  async updatePublicIssue(
    issueId: string,
    data: UpdatePublicIssueRequest
  ): Promise<{
    success: boolean;
    data: Issue;
  }> {
    const response = await this.client.put(`/issues/${issueId}`, data);
    return response.data;
  }

  /**
   * Get issue statistics for tenant dashboard (admin only)
   */
  async getIssueStats(): Promise<{
    success: boolean;
    data: PublicIssueStats;
  }> {
    const response = await this.client.get('/issues/stats');
    return response.data;
  }

  // ============================================================================
  // USER PREFERENCES
  // ============================================================================

  /**
   * Get current user preferences
   */
  async getUserPreferences(): Promise<{
    success: boolean;
    data: {
      id: string;
      user_id: string;
      tenant_id: string;
      email_notifications: boolean;
      browser_notifications: boolean;
      weekly_summary: boolean;
      preferences: Record<string, any>;
      created_at: string;
      updated_at: string;
    };
  }> {
    console.log('🔵 API: Fetching user preferences...');
    const response = await this.client.get('/users/preferences');
    console.log('✅ API: User preferences fetched');
    return response.data;
  }

  /**
   * Update current user preferences
   */
  async updateUserPreferences(data: {
    email_notifications?: boolean;
    browser_notifications?: boolean;
    weekly_summary?: boolean;
    preferences?: Record<string, any>;
  }): Promise<{
    success: boolean;
    data: {
      id: string;
      user_id: string;
      tenant_id: string;
      email_notifications: boolean;
      browser_notifications: boolean;
      weekly_summary: boolean;
      preferences: Record<string, any>;
      created_at: string;
      updated_at: string;
    };
    message: string;
  }> {
    console.log('🔵 API: Updating user preferences...', data);
    const response = await this.client.put('/users/preferences', data);
    console.log('✅ API: User preferences updated');
    return response.data;
  }

  /**
   * Upload user avatar
   */
  async uploadAvatar(file: File): Promise<{
    success: boolean;
    avatar_url: string;
    message: string;
    user: User;
  }> {
    console.log('🔵 API: Uploading avatar...', file.name);
    const formData = new FormData();
    formData.append('avatar', file);

    const response = await this.client.post('/users/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    console.log('✅ API: Avatar uploaded');
    return response.data;
  }

  // ==========================================================================
  // Submission Comments
  // ==========================================================================

  /**
   * Create a comment on a submission
   */
  async createComment(submissionId: string, data: { comment: string; is_internal: boolean }): Promise<SubmissionComment> {
    console.log('🔵 API: Creating comment on submission', submissionId);
    const response = await this.client.post(`/submissions/${submissionId}/comments`, data);
    console.log('✅ API: Comment created');
    return response.data;
  }

  /**
   * List comments for a submission
   */
  async listComments(
    submissionId: string,
    params?: { include_internal?: boolean; limit?: number; offset?: number }
  ): Promise<CommentsListResponse> {
    console.log('🔵 API: Listing comments for submission', submissionId);
    const response = await this.client.get(`/submissions/${submissionId}/comments`, { params });
    console.log('✅ API: Comments retrieved', response.data.total);
    return response.data;
  }

  /**
   * Update a comment
   */
  async updateComment(
    submissionId: string,
    commentId: string,
    data: { comment?: string; is_internal?: boolean }
  ): Promise<SubmissionComment> {
    console.log('🔵 API: Updating comment', commentId);
    const response = await this.client.put(`/submissions/${submissionId}/comments/${commentId}`, data);
    console.log('✅ API: Comment updated');
    return response.data;
  }

  /**
   * Delete a comment
   */
  async deleteComment(submissionId: string, commentId: string): Promise<void> {
    console.log('🔵 API: Deleting comment', commentId);
    await this.client.delete(`/submissions/${submissionId}/comments/${commentId}`);
    console.log('✅ API: Comment deleted');
  }

  /**
   * Get approval history for a submission
   */
  async getApprovalHistory(submissionId: string): Promise<ApprovalHistoryResponse> {
    console.log('🔵 API: Getting approval history for submission', submissionId);
    const response = await this.client.get(`/submissions/${submissionId}/approval-history`);
    console.log('✅ API: Approval history retrieved', response.data.total);
    return response.data;
  }

  // ============================================================================
  // FILE UPLOAD METHODS
  // ============================================================================

  /**
   * Get a presigned upload URL for direct browser-to-storage uploads
   */
  async getPresignedUploadURL(request: PresignedUploadRequest): Promise<PresignedUploadResponse> {
    console.log('🔵 API: Getting presigned upload URL for', request.filename);
    const response = await this.client.post('/uploads/presigned-upload-url', request);
    console.log('✅ API: Presigned upload URL generated');
    return response.data;
  }

  /**
   * Get a presigned download URL for secure file downloads
   */
  async getPresignedDownloadURL(request: PresignedDownloadRequest): Promise<PresignedDownloadResponse> {
    console.log('🔵 API: Getting presigned download URL');
    const response = await this.client.get('/uploads/presigned-download-url', {
      params: {
        file_url: request.file_url,
        expires_in: request.expires_in || 3600,
      },
    });
    console.log('✅ API: Presigned download URL generated');
    return response.data;
  }

  /**
   * Upload file directly via multipart form (alternative to presigned URLs)
   */
  async uploadFileMultipart(file: File): Promise<FileUploadResponse> {
    console.log('🔵 API: Uploading file via multipart', file.name);
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post('/uploads', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    console.log('✅ API: File uploaded', response.data.file_url);
    return response.data;
  }

  /**
   * Get file information
   */
  async getFileInfo(fileUrl: string): Promise<FileInfo> {
    console.log('🔵 API: Getting file info');
    const response = await this.client.get('/uploads/info', {
      params: { file_url: fileUrl },
    });
    console.log('✅ API: File info retrieved');
    return response.data;
  }

  /**
   * List files with optional prefix filter
   */
  async listFiles(request?: ListFilesRequest): Promise<ListFilesResponse> {
    console.log('🔵 API: Listing files');
    const response = await this.client.get('/uploads/list', {
      params: request,
    });
    console.log('✅ API: Files listed', response.data.count);
    return response.data;
  }

  /**
   * Delete a file
   */
  async deleteFile(fileUrl: string): Promise<void> {
    console.log('🔵 API: Deleting file');
    await this.client.delete('/uploads', {
      params: { file_url: fileUrl },
    });
    console.log('✅ API: File deleted');
  }

  // ============================================================================
  // ADMIN CONSOLE (Internal Developer Portal)
  // ============================================================================

  /**
   * Log a system error
   */
  async logError(request: import('../types/admin').CreateSystemErrorRequest): Promise<import('../types/admin').SystemError> {
    const response = await this.client.post('/admin/errors', request);
    return response.data;
  }

  /**
   * List system errors with filters
   */
  async listErrors(filters?: import('../types/admin').ErrorListFilters): Promise<import('../types/admin').ErrorListResponse> {
    const response = await this.client.get('/admin/errors', { params: filters });
    return response.data;
  }

  /**
   * Get a single error by ID
   */
  async getError(errorId: string): Promise<import('../types/admin').SystemError> {
    const response = await this.client.get(`/admin/errors/${errorId}`);
    return response.data;
  }

  /**
   * Update an error (resolve, change status, add notes)
   */
  async updateError(errorId: string, request: import('../types/admin').UpdateSystemErrorRequest): Promise<void> {
    await this.client.patch(`/admin/errors/${errorId}`, request);
  }

  /**
   * Get critical errors
   */
  async getCriticalErrors(): Promise<import('../types/admin').SystemError[]> {
    const response = await this.client.get('/admin/errors/critical');
    return response.data;
  }

  /**
   * Get error counts (by severity, type, status)
   */
  async getErrorsCount(tenantId?: string, since?: string): Promise<import('../types/admin').ErrorCountResponse> {
    const response = await this.client.get('/admin/errors/count', {
      params: { tenant_id: tenantId, since },
    });
    return response.data;
  }

  /**
   * List audit logs with filters
   */
  async listAuditLogs(filters?: import('../types/admin').AuditLogFilters): Promise<import('../types/admin').AuditLogListResponse> {
    const response = await this.client.get('/admin/audit-logs', { params: filters });
    return response.data;
  }

  /**
   * Record a system health metric
   */
  async recordMetric(request: import('../types/admin').CreateSystemHealthMetricRequest): Promise<void> {
    await this.client.post('/admin/metrics', request);
  }

  /**
   * Get system health metrics
   */
  async getMetrics(filters?: import('../types/admin').MetricFilters): Promise<import('../types/admin').SystemHealthMetric[]> {
    const response = await this.client.get('/admin/metrics', { params: filters });
    return response.data;
  }

  /**
   * Get all tenants health overview
   */
  async getAllTenantsHealth(): Promise<import('../types/admin').TenantHealth[]> {
    const response = await this.client.get('/admin/tenants/health');
    return response.data;
  }

  /**
   * Get tenant statistics
   */
  async getTenantStats(tenantId: string): Promise<import('../types/admin').TenantStats> {
    const response = await this.client.get(`/admin/tenants/${tenantId}/stats`);
    return response.data;
  }

  /**
   * Refresh tenant statistics cache
   */
  async refreshTenantStats(tenantId: string): Promise<void> {
    await this.client.post(`/admin/tenants/${tenantId}/stats/refresh`);
  }

  /**
   * Get admin activity summary
   */
  async getAdminActivitySummary(): Promise<import('../types/admin').AdminActivitySummary[]> {
    const response = await this.client.get('/admin/activity-summary');
    return response.data;
  }

  // ============================================================================
  // ALERTING SYSTEM
  // ============================================================================

  /**
   * List alert rules
   */
  async listAlertRules(filters?: import('../types/admin').AlertRuleFilters): Promise<import('../types/admin').AlertRuleListResponse> {
    const response = await this.client.get('/admin/alerts/rules', { params: filters });
    return response.data;
  }

  /**
   * Get alert rule by ID
   */
  async getAlertRule(ruleId: string): Promise<import('../types/admin').AlertRule> {
    const response = await this.client.get(`/admin/alerts/rules/${ruleId}`);
    return response.data;
  }

  /**
   * Create alert rule
   */
  async createAlertRule(request: import('../types/admin').CreateAlertRuleRequest): Promise<import('../types/admin').AlertRule> {
    const response = await this.client.post('/admin/alerts/rules', request);
    return response.data;
  }

  /**
   * Update alert rule
   */
  async updateAlertRule(ruleId: string, request: import('../types/admin').UpdateAlertRuleRequest): Promise<void> {
    await this.client.patch(`/admin/alerts/rules/${ruleId}`, request);
  }

  /**
   * Delete alert rule
   */
  async deleteAlertRule(ruleId: string): Promise<void> {
    await this.client.delete(`/admin/alerts/rules/${ruleId}`);
  }

  /**
   * List alert instances
   */
  async listAlertInstances(filters?: import('../types/admin').AlertInstanceFilters): Promise<import('../types/admin').AlertInstanceListResponse> {
    const response = await this.client.get('/admin/alerts/instances', { params: filters });
    return response.data;
  }

  /**
   * Update alert instance
   */
  async updateAlertInstance(instanceId: string, request: import('../types/admin').UpdateAlertInstanceRequest): Promise<void> {
    await this.client.patch(`/admin/alerts/instances/${instanceId}`, request);
  }

  /**
   * Acknowledge alert
   */
  async acknowledgeAlert(instanceId: string): Promise<void> {
    await this.client.post(`/admin/alerts/instances/${instanceId}/acknowledge`);
  }

  /**
   * Resolve alert
   */
  async resolveAlert(instanceId: string, resolutionNotes?: string): Promise<void> {
    await this.client.post(`/admin/alerts/instances/${instanceId}/resolve`, { resolution_notes: resolutionNotes });
  }

  /**
   * Get active alert counts by severity
   */
  async getAlertCounts(): Promise<import('../types/admin').AlertCountResponse> {
    const response = await this.client.get('/admin/alerts/count');
    return response.data;
  }

  /**
   * Get alerts summary
   */
  async getAlertsSummary(): Promise<import('../types/admin').AlertSummary> {
    const response = await this.client.get('/admin/alerts/summary');
    return response.data;
  }

  /**
   * Manually trigger alert evaluation
   */
  async evaluateAlerts(): Promise<{ message: string }> {
    const response = await this.client.post('/admin/alerts/evaluate');
    return response.data;
  }

  // ============================================================================
  // PUSH NOTIFICATIONS
  // ============================================================================

  /**
   * Get VAPID public key for push subscription
   */
  async getVAPIDPublicKey(): Promise<{ public_key: string }> {
    console.log('🔵 API: Getting VAPID public key');
    const response = await this.client.get('/push/vapid-public-key');
    console.log('✅ API: VAPID public key retrieved');
    return response.data;
  }

  /**
   * Subscribe to push notifications
   */
  async subscribeToPush(subscription: {
    endpoint: string;
    p256dh: string;
    auth: string;
    device_id?: string;
    user_agent?: string;
  }): Promise<{ success: boolean; device_id: string; message: string }> {
    console.log('🔵 API: Subscribing to push notifications');
    const response = await this.client.post('/push/subscribe', subscription);
    console.log('✅ API: Push subscription created');
    return response.data;
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribeFromPush(request: { device_id: string }): Promise<{ success: boolean; message: string }> {
    console.log('🔵 API: Unsubscribing from push notifications');
    const response = await this.client.delete('/push/subscribe', { data: request });
    console.log('✅ API: Push subscription removed');
    return response.data;
  }

  /**
   * Send a test push notification to the current user
   */
  async sendTestPushNotification(): Promise<{ success: boolean; message: string }> {
    console.log('🔵 API: Sending test push notification');
    const response = await this.client.post('/push/test');
    console.log('✅ API: Test push notification sent');
    return response.data;
  }

  // ============================================================================
  // FORM ASSIGNMENTS (Independent/Standalone Forms)
  // ============================================================================

  /**
   * Create a form assignment
   */
  async createFormAssignment(
    data: import('../types').CreateFormAssignmentRequest
  ): Promise<import('../types').FormAssignment> {
    console.log('🔵 API: Creating form assignment');
    const response = await this.client.post('/form-assignments', data);
    console.log('✅ API: Form assignment created');
    return response.data;
  }

  /**
   * Get a form assignment by ID
   */
  async getFormAssignment(id: string): Promise<import('../types').FormAssignment> {
    console.log('🔵 API: Getting form assignment', id);
    const response = await this.client.get(`/form-assignments/${id}`);
    console.log('✅ API: Form assignment retrieved');
    return response.data;
  }

  /**
   * List form assignments with optional filters
   */
  async listFormAssignments(
    params?: import('../types').FormAssignmentFilter
  ): Promise<import('../types').FormAssignmentListResponse> {
    console.log('🔵 API: Listing form assignments');
    const response = await this.client.get('/form-assignments', { params });
    console.log('✅ API: Form assignments retrieved', response.data.total);
    return response.data;
  }

  /**
   * Get pending forms for the current user
   */
  async getMyPendingForms(
    params?: { role?: string; department?: string }
  ): Promise<{ pending_forms: import('../types').UserPendingForm[]; count: number }> {
    console.log('🔵 API: Getting my pending forms');
    const response = await this.client.get('/form-assignments/my-pending', { params });
    console.log('✅ API: Pending forms retrieved', response.data.count);
    return response.data;
  }

  /**
   * Cancel a form assignment
   */
  async cancelFormAssignment(
    id: string,
    data?: import('../types').CancelFormAssignmentRequest
  ): Promise<{ message: string }> {
    console.log('🔵 API: Cancelling form assignment', id);
    const response = await this.client.delete(`/form-assignments/${id}`, { data });
    console.log('✅ API: Form assignment cancelled');
    return response.data;
  }

  /**
   * Create a public form token
   */
  async createPublicFormToken(
    data: import('../types').CreatePublicFormTokenRequest
  ): Promise<import('../types').PublicFormToken> {
    console.log('🔵 API: Creating public form token');
    const response = await this.client.post('/form-assignments/public-tokens', data);
    console.log('✅ API: Public form token created');
    return response.data;
  }

  /**
   * Get a public form by token (NO AUTH REQUIRED)
   */
  async getPublicForm(token: string): Promise<import('../types').PublicFormResponse> {
    console.log('🔵 API: Getting public form by token');
    const response = await this.client.get(`/public/forms/${token}`);
    console.log('✅ API: Public form retrieved');
    return response.data;
  }

  /**
   * Submit a public form (NO AUTH REQUIRED)
   */
  async submitPublicForm(
    token: string,
    data: {
      data: Record<string, any>;
      submitter_name?: string;
      submitter_email?: string;
    }
  ): Promise<{ message: string; form_template_id: string; redirect_url?: string }> {
    console.log('🔵 API: Submitting public form');
    const response = await this.client.post(`/public/forms/${token}/submit`, data);
    console.log('✅ API: Public form submitted');
    return response.data;
  }

  // ============================================================================
  // ISSUE TYPES (per-tenant taxonomy)
  // ============================================================================

  /**
   * List all issue types for the current tenant
   */
  async listIssueTypes(): Promise<IssueTypeDef[]> {
    const response = await this.client.get('/issue-types');
    return response.data;
  }

  /**
   * Create a new issue type for the current tenant
   */
  async createIssueType(req: IssueTypeCreateRequest): Promise<IssueTypeDef> {
    const response = await this.client.post('/issue-types', req);
    return response.data;
  }

  /**
   * Update an existing issue type
   */
  async updateIssueType(id: string, req: IssueTypeUpdateRequest): Promise<IssueTypeDef> {
    const response = await this.client.patch(`/issue-types/${id}`, req);
    return response.data;
  }

  /**
   * Delete an issue type by ID
   */
  async deleteIssueType(id: string): Promise<void> {
    await this.client.delete(`/issue-types/${id}`);
  }

  // ============================================================================
  // ESCALATION CHAINS
  // ============================================================================

  /**
   * List all escalation chains for the current tenant
   */
  async listEscalationChains(): Promise<EscalationChain[]> {
    const response = await this.client.get('/escalation-chains');
    return response.data;
  }

  /**
   * Get a single escalation chain by ID (includes steps)
   */
  async getEscalationChain(id: string): Promise<EscalationChain> {
    const response = await this.client.get(`/escalation-chains/${id}`);
    return response.data;
  }

  /**
   * Create a new escalation chain (with steps)
   */
  async createEscalationChain(req: CreateChainRequest): Promise<EscalationChain> {
    const response = await this.client.post('/escalation-chains', req);
    return response.data;
  }

  /**
   * Delete an escalation chain by ID
   */
  async deleteEscalationChain(id: string): Promise<void> {
    await this.client.delete(`/escalation-chains/${id}`);
  }

  // ============================================================================
  // RESOLUTION PREVIEW
  // ============================================================================

  /**
   * Preview which escalation chain and recipients would be used for a given
   * asset type / issue type / site combination
   */
  async resolutionPreview(params: {
    asset_type_id?: string;
    issue_type_key?: string;
    site_id?: string;
  }): Promise<ResolutionPreview> {
    const response = await this.client.get('/issues/resolution-preview', { params });
    return response.data;
  }

  // ============================================================================
  // ASSET-TYPE ELIGIBILITY + PER-ASSET OVERRIDE
  // ============================================================================

  /**
   * Update issue-reporter eligibility settings for an asset type
   */
  async updateAssetTypeEligibility(
    assetTypeID: string,
    req: AssetTypeEligibilityUpdate,
  ): Promise<void> {
    await this.client.patch(`/asset-types/${assetTypeID}/issue-reporter-eligibility`, req);
  }

  /**
   * Update per-asset issue-reporting override (pass null fields to clear the override)
   */
  async updateAssetIssueReportingOverride(
    assetID: string,
    req: AssetReporterOverrideUpdate,
  ): Promise<void> {
    await this.client.patch(`/assets/${assetID}/issue-reporter-override`, req);
  }
}

// ============================================================================
// EXPORT SINGLETON INSTANCE (with lazy initialization for SSR safety)
// ============================================================================

let apiServiceInstance: ApiService | null = null;

const getApiService = (): ApiService => {
  if (!apiServiceInstance) {
    apiServiceInstance = new ApiService();
  }
  return apiServiceInstance;
};

// Create a Proxy that lazily initializes the service on first property access
// This prevents ApiService constructor from running during webpack build
const apiService = new Proxy({} as ApiService, {
  get(_target, prop) {
    const instance = getApiService();
    const value = (instance as any)[prop];
    // Bind methods to the instance
    return typeof value === 'function' ? value.bind(instance) : value;
  },
});

export default apiService;
